// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which login a chat, model or plan uses when there are two of one agent. T3 runs each
// login as its own provider instance with its own name ("Claude · Nuvio", "Claude · Personal");
// Konvo marks them with the name's first letter on the logo and the name in a pill. With one
// login of an agent nothing is marked.

import type {AgentMachineView, AgentModelChoice, AgentThreadItem} from '@fluxer/schema/src/domains/konvo/Agents';
import {
	type AgentPlanUsageView,
	type AgentUsageWindowView,
	agentFamily,
	agentThreadPlan,
	tightestAgentWindow,
} from '@fluxer/schema/src/domains/konvo/AgentUsage';

export interface AgentAccount {
	/** The instance's name without the brand: "Personal" from "Claude · Personal". */
	label: string;
	/** The label's first letter, for the logo's corner. */
	letter: string;
	/** One of four colours, by the label's place among the agent's logins. */
	tone: number;
	/** The colour chosen in the provider's settings, when present. */
	color?: string;
}

export const validAccountColor = (value: string | undefined): string | undefined =>
	value && /^#[0-9a-f]{6}$/i.test(value) ? value : undefined;

/** A plan is shared across machines; use the newest reporting machine's saved account colour. */
export function planAccentColor(
	plan: Pick<AgentPlanUsageView, 'key' | 'provider'>,
	machines: ReadonlyArray<AgentMachineView>,
): string | undefined {
	return machines
		.flatMap((machine) =>
			machine.tools.flatMap((tool) => {
				const owns = tool.usage?.some(
					(usage) =>
						(usage.account ? `${usage.provider}:${usage.account}` : `${usage.provider}@${machine.name}`) === plan.key,
				);
				return owns ? (tool.connections ?? []).filter((c) => c.provider === plan.provider) : [];
			}),
		)
		.sort((a, b) => Date.parse(b.checkedAt) - Date.parse(a.checkedAt))
		.map((c) => validAccountColor(c.config?.accentColor))
		.find((color) => color !== undefined);
}

export const ACCOUNT_TONES = 4;

const BRAND = /^(claude code|claude|codex|chatgpt|openai|anthropic)(?=$|[^a-z0-9])/i;

/** "Personal" from "Claude · Personal" or "Claude (Personal)"; the whole name when it is only the brand. */
export function accountLabel(name: string, fallback = ''): string {
	const trimmed = name.trim();
	const rest = trimmed
		.replace(BRAND, '')
		.replace(/^[\s·•:|/(\-–—]+/, '')
		.replace(/[\s)]+$/, '')
		.trim();
	return rest || trimmed || fallback;
}

interface AccountEntry {
	/** What the account is looked up by: an instance id, or a plan key. */
	id: string;
	/** The provider instance, for the agent family. */
	provider: string;
	name: string;
	color?: string;
}

/** An account for every entry whose agent has two or more of them; none for the rest. */
export function accountsOf(entries: Iterable<AccountEntry>): Map<string, AgentAccount> {
	const families = new Map<string, Array<{id: string; label: string; color?: string}>>();
	const seen = new Set<string>();
	for (const entry of entries) {
		if (seen.has(entry.id)) continue;
		seen.add(entry.id);
		const family = agentFamily(entry.provider);
		const list = families.get(family) ?? [];
		list.push({id: entry.id, label: accountLabel(entry.name, entry.provider), color: entry.color});
		families.set(family, list);
	}
	const out = new Map<string, AgentAccount>();
	for (const list of families.values()) {
		if (list.length < 2) continue;
		const labels = [...new Set(list.map((entry) => entry.label))].sort((a, b) => a.localeCompare(b));
		for (const entry of list) {
			out.set(entry.id, {
				label: entry.label,
				letter: (Array.from(entry.label)[0] ?? '?').toUpperCase(),
				tone: labels.indexOf(entry.label) % ACCOUNT_TONES,
				...(entry.color ? {color: entry.color} : {}),
			});
		}
	}
	return out;
}

/** The logins on one machine, by provider instance: from the models it offers and the plans it reports. */
export function machineAccounts(
	machine: Pick<AgentMachineView, 'tools'> | null | undefined,
): Map<string, AgentAccount> {
	const entries: Array<AccountEntry> = [];
	for (const tool of machine?.tools ?? []) {
		for (const connection of tool.connections ?? [])
			entries.push({
				id: connection.provider,
				provider: connection.provider,
				name: connection.providerName,
				color: validAccountColor(connection.config?.accentColor),
			});
		for (const model of tool.models ?? [])
			entries.push({id: model.provider, provider: model.provider, name: model.providerName});
		for (const usage of tool.usage ?? [])
			entries.push({id: usage.provider, provider: usage.provider, name: usage.providerName});
	}
	return accountsOf(entries);
}

/** The person's plans, by plan key, marked when one agent has two or more of them. */
export function planAccounts(
	plans: ReadonlyArray<Pick<AgentPlanUsageView, 'key' | 'provider' | 'providerName'>>,
	machines: ReadonlyArray<AgentMachineView> = [],
) {
	return accountsOf(
		plans.map((plan) => ({
			id: plan.key,
			provider: plan.provider,
			name: plan.providerName,
			color: planAccentColor(plan, machines),
		})),
	);
}

/** A plan with a window used up (and not reset since the last check). */
export function planIsOut(plan: Pick<AgentPlanUsageView, 'windows'>): boolean {
	return plan.windows.some((window) => window.usedPercent >= 100 && !window.reset);
}

/** Another login of the same agent on the machine that still has room. */
export interface OtherLogin {
	/** Its provider instance. */
	instance: string;
	/** Its name as the tool shows it, e.g. "Claude · Personal". */
	name: string;
	account: AgentAccount | null;
	plan: AgentPlanUsageView;
	/** Its fullest window, for "78% of its session left". */
	window: AgentUsageWindowView | null;
	/** The same model on that login (else its default), to start a chat with. */
	choice: AgentModelChoice;
	/** The used-up login's name, e.g. "Claude · Nuvio". */
	fromName: string;
}

/**
 * When the login `from` runs on has a used-up plan, the machine's other login of that agent with
 * the most room, and the model to use there. Null when the plan has room, when there is no other
 * login, or when the others are used up too.
 */
export function otherLoginWithRoom(
	plans: ReadonlyArray<AgentPlanUsageView>,
	machine: AgentMachineView | null | undefined,
	from: {instance: string; model?: string | null; effort?: string | null},
): OtherLogin | null {
	if (!machine) return null;
	const family = agentFamily(from.instance);
	const fromPlan = agentThreadPlan(plans, machine, {provider: family, instance: from.instance});
	if (!fromPlan || !planIsOut(fromPlan)) return null;
	const models = machine.tools.flatMap((tool) => tool.models ?? []);
	const accounts = machineAccounts(machine);
	let best: OtherLogin | null = null;
	for (const instance of new Set(models.map((model) => model.provider))) {
		if (instance === from.instance || agentFamily(instance) !== family) continue;
		const plan = agentThreadPlan(plans, machine, {provider: family, instance});
		if (!plan || plan.key === fromPlan.key || planIsOut(plan)) continue;
		const window = tightestAgentWindow(plan);
		if (best && (best.window?.usedPercent ?? 0) <= (window?.usedPercent ?? 0)) continue;
		const own = models.filter((model) => model.provider === instance);
		const model = own.find((entry) => entry.model === from.model) ?? own.find((entry) => entry.isDefault) ?? own[0];
		if (!model) continue;
		best = {
			instance,
			name: model.providerName,
			account: accounts.get(instance) ?? null,
			plan,
			window,
			// The same reasoning level, when the model there has it.
			choice: {
				provider: instance,
				model: model.model,
				...(from.effort && model.efforts?.includes(from.effort) ? {effort: from.effort} : {}),
			},
			fromName: fromPlan.providerName,
		};
	}
	return best;
}

const HANDOFF_PART = 1500;
const clip = (text: string, max = HANDOFF_PART) => {
	const value = text.trim().replace(/\n{3,}/g, '\n\n');
	return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

/**
 * What a new chat on another login needs to carry on with a chat that stopped: the asks, the last
 * reply and the branch. For the agent, so in English like the rest of a prompt's context.
 */
export function handoffText(thread: {
	title: string;
	branch?: string | null;
	items?: ReadonlyArray<AgentThreadItem>;
}): string {
	const items = thread.items ?? [];
	const asks = items.filter((item) => item.kind === 'user' && item.text.trim());
	const reply = items.findLast((item) => item.kind === 'assistant' && item.text.trim());
	const lines = [
		`The chat "${thread.title}" stopped when its plan ran out.${thread.branch ? ` It worked on the branch ${thread.branch}.` : ''}`,
	];
	const first = asks[0];
	const last = asks.at(-1);
	if (first && first.kind === 'user') lines.push('', 'It started with:', clip(first.text));
	if (last && last !== first && last.kind === 'user') lines.push('', 'The latest request was:', clip(last.text));
	if (reply && reply.kind === 'assistant') lines.push('', 'Its last reply:', clip(reply.text));
	return lines.join('\n');
}
